import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["notification"]

  connect() {
    this.handleGlobalFlash = this.handleGlobalFlash.bind(this)
    window.addEventListener("flash:show", this.handleGlobalFlash)
    
    // Initialize all existing notifications
    this.notificationTargets.forEach(target => {
      this.initializeNotification(target)
    })
  }

  disconnect() {
    window.removeEventListener("flash:show", this.handleGlobalFlash)
  }

  handleGlobalFlash(event) {
    const { message, type } = event.detail
    this.show(message, type)
  }

  initializeNotification(element) {
    // Add manual dismiss listener if not already present
    if (!element.dataset.initialized) {
      element.addEventListener('dismiss-flash', () => this.dismiss(element))
      
      // Touch Swipe Logic
      let startX = 0
      let currentX = 0
      let isSwiping = false

      element.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX
        element.style.transition = 'none'
        isSwiping = true
      }, { passive: true })

      element.addEventListener('touchmove', (e) => {
        if (!isSwiping) return
        currentX = e.touches[0].clientX
        const deltaX = currentX - startX
        
        // Only allow swiping to the left (R->L)
        if (deltaX < 0) {
          element.style.transform = `translateX(${deltaX}px)`
          element.style.opacity = 1 + (deltaX / 300)
        }
      }, { passive: true })

      element.addEventListener('touchend', (e) => {
        isSwiping = false
        const deltaX = currentX - startX
        
        if (deltaX < -100) {
          // Swipe threshold met
          element.style.transition = 'all 0.3s ease-out'
          element.style.transform = 'translateX(-120%)'
          element.style.opacity = '0'
          setTimeout(() => this.dismiss(element), 200)
        } else {
          // Snap back
          element.style.transition = 'all 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28)'
          element.style.transform = 'translateX(0)'
          element.style.opacity = '1'
        }
      }, { passive: true })

      element.dataset.initialized = "true"
      this.scheduleDismiss(element)
    }
  }

  manualDismiss(event) {
    const notification = event.currentTarget.closest('[data-flash-target="notification"]')
    if (notification) {
      this.dismiss(notification)
    }
  }

  show(message, type = "notice") {
    const id = `flash-${Math.random().toString(36).substr(2, 9)}`
    const isNotice = type === 'notice'
    const colorClass = isNotice ? 'bg-green-600' : 'bg-red-600'
    const iconBgClass = isNotice ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
    const label = isNotice ? 'Success' : 'Attention'
    const labelColorClass = isNotice ? 'text-green-600' : 'text-red-600'
    
    const icon = isNotice 
      ? '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path></svg>'
      : '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>'

    const flashHtml = `
      <div id="${id}" data-flash-target="notification" class="mb-3 pointer-events-auto group transform transition-all duration-300 ease-out translate-y-[-20px] opacity-0 swipe-ignore">
        <div class="relative overflow-hidden bg-white rounded-2xl shadow-[0_15px_30px_rgba(0,0,0,0.2)] border border-gray-100 p-4 flex items-center gap-3">
          <div class="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${iconBgClass}">
            ${icon}
          </div>
          <div class="flex-grow">
            <p class="text-[9px] font-black uppercase tracking-widest ${labelColorClass} mb-0.5">${label}</p>
            <p class="text-xs font-bold text-gray-700 leading-tight">${message}</p>
          </div>
          <button type="button" 
                  data-action="click->flash#manualDismiss"
                  class="flex-shrink-0 text-gray-300 hover:text-gray-600 transition-colors cursor-pointer relative z-50 p-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div class="absolute bottom-0 left-0 h-1 opacity-20 animate-progress ${colorClass}"></div>
        </div>
      </div>
    `
    
    this.element.insertAdjacentHTML("afterbegin", flashHtml)
    const newFlash = this.element.querySelector(`#${id}`)
    
    // Initialize touch and dismiss logic
    this.initializeNotification(newFlash)
    
    // Animate in
    requestAnimationFrame(() => {
      newFlash.classList.remove("opacity-0", "translate-y-[-20px]")
      newFlash.classList.add("opacity-100", "translate-y-0")
    })
  }

  scheduleDismiss(element) {
    if (element.dataset.dismissScheduled) return
    element.dataset.dismissScheduled = "true"
    
    setTimeout(() => {
      this.dismiss(element)
    }, 5000)
  }

  dismiss(element) {
    if (!element || !element.parentNode || element.dataset.dismissing) return
    element.dataset.dismissing = "true"

    element.style.transition = 'all 0.3s ease-out'
    element.style.opacity = '0'
    element.style.transform = element.style.transform + ' translateY(-20px)'
    
    setTimeout(() => {
      if (element.parentNode) {
        element.remove()
      }
    }, 300)
  }
}
